import React from 'react';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';

import SendPaylinkForm from './index';
import { useGetConfigsQuery, useGetPublicMerchantQuery } from '../../../store/modules/merchant/api';
import { PartialPaymentValue } from '../../../store/modules/merchant/types';
import { isPartialPaymentEnabled } from '../../../util/partialPaymentConfig';
import { usePaylinkSubmit } from '../hooks';
import { useMerchant } from '../../../contexts/MerchantContext';
import { DialogCloseButton } from '../../../components/core/DialogCloseButton';
import { PaylinkCustomerInfoResponse } from '../../../store/modules/paylink/types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  preSelectedCustomer?: PaylinkCustomerInfoResponse;
}

/**
 * @summary Modal component to send a new paylink
 * @param {Props} props Component properties
 * @returns {JSX.Element} send paylink modal
 */
const SendPaylinkModal = ({ isOpen, onClose, preSelectedCustomer }: Props) => {
  const { merchantId, merchantName: name } = useMerchant();
  const { data: publicData } = useGetPublicMerchantQuery(merchantId, { skip: !merchantId });
  const { data: configs } = useGetConfigsQuery(merchantId, { skip: !merchantId });
  const partialPaymentConfig = configs?.find((c) => c.name === 'PARTIAL_PAYMENTS') as
    { value?: PartialPaymentValue } | undefined;
  const merchantName = publicData?.branding?.displayName ?? name;

  const {
    onPaylinkSubmit,
    isLoading: isLoadingSubmit,
    serverValidationError,
  } = usePaylinkSubmit({ merchantId, onClose });

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 4,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>Send a new Paylink</DialogTitle>
      <DialogCloseButton onClose={onClose} />
      <DialogContent sx={{ p: 0, overflowX: 'hidden' }} dividers>
        <SendPaylinkForm
          merchantName={merchantName}
          merchantId={merchantId}
          onSubmit={onPaylinkSubmit}
          isLoadingSubmit={isLoadingSubmit}
          serverValidationError={serverValidationError}
          isModal
          currency={publicData?.acceptedCurrency}
          isPartialPaymentCheckboxEnabled={isPartialPaymentEnabled(partialPaymentConfig)}
          preSelectedCustomer={preSelectedCustomer}
        />
      </DialogContent>
    </Dialog>
  );
};

export default SendPaylinkModal;
